import { type RoomTypeListItem, type RoomTypeListQuery } from '@utazzo/shared';
import { UzStatusBadge } from '@utazzo/ui';
import React from 'react';

import { RoomTypeFormDrawer } from '../components/RoomTypeFormDrawer';
import { listRoomTypes } from '../room-types.api';
import {
  useBulkDeleteRoomTypes,
  useBulkSetRoomTypesStatus,
  useDeleteRoomType,
  useRoomTypesList,
} from '../use-room-types';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';
import { useCurrentUser } from '@/features/auth/use-auth';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const MIN_SEARCH_LENGTH = 2;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const RoomTypesListPage = (): React.JSX.Element => {
  const me = useCurrentUser();
  const canDelete = me?.role === 'ADMIN';
  const deleteRoomType = useDeleteRoomType();
  const bulkDeleteMut = useBulkDeleteRoomTypes();
  const bulkSetStatusMut = useBulkSetRoomTypesStatus();

  const columns: ListScreenColumns<RoomTypeListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<RoomTypeListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.name}</span>
            <span className="text-xs text-zinc-500">{row.code}</span>
          </div>
        ),
      },
      {
        id: 'description',
        header: 'Description',
        cell: (row) => <span className="text-zinc-600">{row.description ?? '-'}</span>,
      },
      {
        id: 'sortOrder',
        header: 'Sort',
        sortable: true,
        width: '80px',
        cell: (row) => <span>{row.sortOrder}</span>,
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => (
          <div className="flex items-center gap-2">
            <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />
          </div>
        ),
      },
    ];
    return cols;
  };

  return (
    <ListScreen<RoomTypeListItem, RoomTypeListQuery>
      title="Room Types"
      description="Hotel room categories. Used when adding HOTEL components to an itinerary."
      useList={useRoomTypesList}
      defaultSort={{ by: 'sortOrder', dir: 'asc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        return {
          ...base,
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add room type' }}
      renderDrawer={({ open, mode, row, close }) => (
        <RoomTypeFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          roomType={row}
        />
      )}
      rowActions={{
        edit: true,
        delete: {
          onDelete: (row) => deleteRoomType.mutateAsync(row.id),
          disabled: () => !canDelete,
          confirm: (row) => ({
            title: 'Delete this room type?',
            description: `${row.name} will be soft-deleted and hidden from lists. Itineraries already using it are unaffected.`,
          }),
        },
      }}
      bulkActions={{
        delete: {
          onDelete: async (rows) => {
            await bulkDeleteMut.mutateAsync(rows.map((r) => r.id));
          },
          confirm: (rows) => ({
            title: `Delete ${rows.length} room type${rows.length === 1 ? '' : 's'}?`,
            description:
              'These room types will be soft-deleted. Itineraries already using them are unaffected.',
          }),
        },
        setActive: {
          onSetActive: async (rows, isActive) =>
            bulkSetStatusMut.mutateAsync({ ids: rows.map((r) => r.id), isActive }),
          confirm: (rows, isActive) => ({
            title: `${isActive ? 'Activate' : 'Deactivate'} ${rows.length} room type${rows.length === 1 ? '' : 's'}?`,
            description: isActive
              ? 'They will become selectable again in the itinerary editor.'
              : 'They will be hidden from new selections but remain on historical records.',
          }),
        },
      }}
      export={{
        fetchPage: (q) => listRoomTypes(q),
        filename: 'room-types',
        csv: [
          { header: 'Code', value: (r) => r.code },
          { header: 'Name', value: (r) => r.name },
          { header: 'Description', value: (r) => r.description ?? '' },
          { header: 'Sort order', value: (r) => r.sortOrder },
          { header: 'Status', value: (r) => (r.isActive ? 'Active' : 'Inactive') },
        ],
      }}
      copy={{
        searchPlaceholder: 'Search by code, name, or description',
        emptyTitle: 'No room types match these filters',
        emptyDescription: 'Adjust the status filter or clear the search box.',
        errorTitle: 'Failed to load room types',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
